import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  CheckCircle2, XCircle, Edit, Trash2, Eye, ShieldCheck,
  Clock, ListChecks, Users as UsersIcon, Mail, Phone, MapPin, UserMinus,
  Shield, ShieldOff, FileText, HelpCircle, MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/Avatar';
import { useAuth } from '@/contexts/AuthContext';
import { BlogAdminTab } from '@/components/admin/BlogAdminTab';
import { FaqAdminTab } from '@/components/admin/FaqAdminTab';
import { ContactMessagesAdminTab } from '@/components/admin/ContactMessagesAdminTab';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: { bg: '#FEF3C7', color: '#92400E', label: 'Pending' },
  approved: { bg: '#D1FAE5', color: '#065F46', label: 'Approved' },
  rejected: { bg: '#FEE2E2', color: '#991B1B', label: 'Rejected' }
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span
      className="uppercase px-2 py-1 rounded"
      style={{
        backgroundColor: s.bg,
        color: s.color,
        fontFamily: 'Manrope',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.1em'
      }}
    >
      {s.label}
    </span>
  );
};

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('pending');
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [removeUserTarget, setRemoveUserTarget] = useState(null);
  const [roleTarget, setRoleTarget] = useState(null);
  const [contactUnread, setContactUnread] = useState(0);

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, allRes, usersRes, contactRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/listings?status=pending`, { withCredentials: true }),
        axios.get(`${API}/admin/listings`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/contact-messages/unread-count`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setPending(pendingRes.data);
      setAll(allRes.data);
      setUsers(usersRes.data);
      setContactUnread(contactRes.data?.unread || 0);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const approve = async (listing) => {
    setProcessing(listing.id);
    try {
      await axios.patch(`${API}/admin/listings/${listing.id}/approve`, {}, { withCredentials: true });
      toast.success(`Approved: ${listing.title}`);
      await refresh();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const confirmReject = async () => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget.id);
    try {
      await axios.patch(
        `${API}/admin/listings/${rejectTarget.id}/reject`,
        { reason: rejectReason },
        { withCredentials: true }
      );
      toast.success(`Rejected: ${rejectTarget.title}`);
      setRejectTarget(null);
      setRejectReason('');
      await refresh();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setProcessing(deleteTarget.id);
    try {
      await axios.delete(`${API}/listings/${deleteTarget.id}`, { withCredentials: true });
      toast.success(`Deleted: ${deleteTarget.title}`);
      setDeleteTarget(null);
      await refresh();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to delete');
    } finally {
      setProcessing(null);
    }
  };

  const confirmRemoveUser = async () => {
    if (!removeUserTarget) return;
    setProcessing(removeUserTarget.id);
    try {
      await axios.delete(`${API}/admin/users/${removeUserTarget.id}`, { withCredentials: true });
      toast.success(`Removed user: ${removeUserTarget.name || removeUserTarget.email}`);
      setRemoveUserTarget(null);
      await refresh();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to remove user');
    } finally {
      setProcessing(null);
    }
  };

  const confirmRoleChange = async () => {
    if (!roleTarget) return;
    const { user: u, action } = roleTarget;
    setProcessing(u.id);
    try {
      await axios.post(`${API}/admin/users/${u.id}/${action}`, {}, { withCredentials: true });
      toast.success(
        action === 'promote'
          ? `${u.name || u.email} is now an admin`
          : `Admin access revoked for ${u.name || u.email}`
      );
      setRoleTarget(null);
      await refresh();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : `Failed to ${action}`);
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={32} strokeWidth={1.5} style={{ color: '#D95A41' }} />
          <h1
            className="text-4xl sm:text-5xl tracking-tight"
            style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
            data-testid="admin-heading"
          >
            Admin Dashboard
          </h1>
        </div>
        <p className="mb-10" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
          Manage listings, review submissions, and moderate the community.
        </p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            <StatCard icon={<Clock size={20} strokeWidth={1.5} />} label="Pending" value={stats.pending} color="#92400E" bg="#FEF3C7" />
            <StatCard icon={<CheckCircle2 size={20} strokeWidth={1.5} />} label="Approved" value={stats.approved} color="#065F46" bg="#D1FAE5" />
            <StatCard icon={<XCircle size={20} strokeWidth={1.5} />} label="Rejected" value={stats.rejected} color="#991B1B" bg="#FEE2E2" />
            <StatCard icon={<UsersIcon size={20} strokeWidth={1.5} />} label="Total Users" value={stats.total_users} color="#1A1A1A" bg="#F2EFEB" />
          </div>
        )}

        <div className="flex gap-2 mb-6 border-b border-[#E5E0D8] overflow-x-auto">
          <TabBtn active={activeTab === 'pending'} onClick={() => setActiveTab('pending')} testId="tab-pending">
            <Clock size={16} strokeWidth={1.5} className="mr-2" />
            Pending Approvals ({pending.length})
          </TabBtn>
          <TabBtn active={activeTab === 'all'} onClick={() => setActiveTab('all')} testId="tab-all">
            <ListChecks size={16} strokeWidth={1.5} className="mr-2" />
            All Listings ({all.length})
          </TabBtn>
          <TabBtn active={activeTab === 'users'} onClick={() => setActiveTab('users')} testId="tab-users">
            <UsersIcon size={16} strokeWidth={1.5} className="mr-2" />
            Users ({users.length})
          </TabBtn>
          <TabBtn active={activeTab === 'blog'} onClick={() => setActiveTab('blog')} testId="tab-blog">
            <FileText size={16} strokeWidth={1.5} className="mr-2" />
            Blog
          </TabBtn>
          <TabBtn active={activeTab === 'faqs'} onClick={() => setActiveTab('faqs')} testId="tab-faqs">
            <HelpCircle size={16} strokeWidth={1.5} className="mr-2" />
            FAQs
          </TabBtn>
          <TabBtn active={activeTab === 'messages'} onClick={() => setActiveTab('messages')} testId="tab-messages">
            <MessageSquare size={16} strokeWidth={1.5} className="mr-2" />
            Messages
            {contactUnread > 0 && (
              <span
                className="ml-2 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor: '#D95A41',
                  color: '#FFFFFF',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 5px',
                  fontFamily: 'Manrope',
                  fontSize: '10px',
                  fontWeight: 700
                }}
                data-testid="messages-tab-badge"
              >
                {contactUnread > 9 ? '9+' : contactUnread}
              </span>
            )}
          </TabBtn>
        </div>

        {loading ? (
          <p className="text-center py-12" style={{ fontFamily: 'Manrope', color: '#5C5C5C' }}>
            Loading...
          </p>
        ) : activeTab === 'pending' ? (
          pending.length === 0 ? (
            <EmptyState message="No pending listings — you're all caught up!" testId="empty-pending" />
          ) : (
            <div className="space-y-3" data-testid="pending-list">
              {pending.map((l) => (
                <AdminListingRow
                  key={l.id}
                  listing={l}
                  actions={
                    <>
                      <Button
                        onClick={() => navigate(`/listing/${l.id}`)}
                        variant="ghost" size="sm"
                        style={{ fontFamily: 'Manrope', fontSize: '13px' }}
                        data-testid={`view-listing-${l.id}`}
                      >
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                      <Button
                        onClick={() => approve(l)}
                        disabled={processing === l.id}
                        className="rounded-md"
                        size="sm"
                        style={{ backgroundColor: '#2E5C41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                        data-testid={`approve-${l.id}`}
                      >
                        <CheckCircle2 size={14} className="mr-1" /> Approve
                      </Button>
                      <Button
                        onClick={() => { setRejectTarget(l); setRejectReason(''); }}
                        disabled={processing === l.id}
                        variant="outline" size="sm"
                        className="rounded-md"
                        style={{ borderColor: '#991B1B', color: '#991B1B', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                        data-testid={`reject-${l.id}`}
                      >
                        <XCircle size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  }
                />
              ))}
            </div>
          )
        ) : activeTab === 'all' ? (
          all.length === 0 ? (
            <EmptyState message="No listings yet." testId="empty-all" />
          ) : (
            <div className="space-y-3" data-testid="all-list">
              {all.map((l) => (
                <AdminListingRow
                  key={l.id}
                  listing={l}
                  actions={
                    <>
                      <Button
                        onClick={() => navigate(`/listing/${l.id}`)}
                        variant="ghost" size="sm"
                        style={{ fontFamily: 'Manrope', fontSize: '13px' }}
                        data-testid={`view-listing-${l.id}`}
                      >
                        <Eye size={14} className="mr-1" /> View
                      </Button>
                      <Button
                        onClick={() => navigate(`/edit-listing/${l.id}`)}
                        variant="outline" size="sm"
                        className="rounded-md"
                        style={{ borderColor: '#D95A41', color: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                        data-testid={`edit-${l.id}`}
                      >
                        <Edit size={14} className="mr-1" /> Edit
                      </Button>
                      <Button
                        onClick={() => setDeleteTarget(l)}
                        disabled={processing === l.id}
                        variant="outline" size="sm"
                        className="rounded-md"
                        style={{ borderColor: '#991B1B', color: '#991B1B', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px' }}
                        data-testid={`delete-${l.id}`}
                      >
                        <Trash2 size={14} className="mr-1" /> Delete
                      </Button>
                    </>
                  }
                />
              ))}
            </div>
          )
        ) : activeTab === 'users' ? (
          users.length === 0 ? (
            <EmptyState message="No users found." testId="empty-users" />
          ) : (
            <UsersTable
              users={users}
              currentUserId={user?.id}
              onRemove={setRemoveUserTarget}
              onRoleChange={(u, action) => setRoleTarget({ user: u, action })}
              processingId={processing}
            />
          )
        ) : activeTab === 'blog' ? (
          <BlogAdminTab />
        ) : activeTab === 'faqs' ? (
          <FaqAdminTab />
        ) : (
          <ContactMessagesAdminTab />
        )}

        {/* Reject dialog */}
        <AlertDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
          <AlertDialogContent data-testid="reject-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                Reject "{rejectTarget?.title}"?
              </AlertDialogTitle>
              <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
                Optionally provide a reason so the cook knows what to fix.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Reason for rejection (optional)"
              className="w-full px-3 py-2 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
              style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
              data-testid="reject-reason-input"
            />
            <AlertDialogFooter>
              <AlertDialogCancel style={{ fontFamily: 'Manrope' }} data-testid="cancel-reject">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmReject}
                style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="confirm-reject"
              >
                Reject Listing
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent data-testid="delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                Delete "{deleteTarget?.title}"?
              </AlertDialogTitle>
              <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
                This permanently removes the listing. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ fontFamily: 'Manrope' }} data-testid="cancel-delete">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="confirm-delete"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Remove user dialog */}
        <AlertDialog open={!!removeUserTarget} onOpenChange={(o) => !o && setRemoveUserTarget(null)}>
          <AlertDialogContent data-testid="remove-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                Remove {removeUserTarget?.name || removeUserTarget?.email}?
              </AlertDialogTitle>
              <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
                This permanently deletes the user's account along with their listings, messages,
                reviews, and notifications. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ fontFamily: 'Manrope' }} data-testid="cancel-remove-user">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemoveUser}
                style={{ backgroundColor: '#991B1B', fontFamily: 'Manrope', fontWeight: 600 }}
                data-testid="confirm-remove-user"
              >
                Remove User
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Promote / Demote admin dialog */}
        <AlertDialog open={!!roleTarget} onOpenChange={(o) => !o && setRoleTarget(null)}>
          <AlertDialogContent data-testid="role-change-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                {roleTarget?.action === 'promote' ? 'Make Admin' : 'Revoke Admin Access'}
              </AlertDialogTitle>
              <AlertDialogDescription style={{ fontFamily: 'Manrope' }}>
                {roleTarget?.action === 'promote'
                  ? <>Grant <strong>{roleTarget?.user?.name || roleTarget?.user?.email}</strong> full admin access?
                      They will be able to approve/reject listings, manage users, and add or remove other admins.</>
                  : <>Revoke admin access for <strong>{roleTarget?.user?.name || roleTarget?.user?.email}</strong>?
                      Their account will remain, but they'll lose access to the Admin Dashboard.</>}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel style={{ fontFamily: 'Manrope' }} data-testid="cancel-role-change">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRoleChange}
                style={{
                  backgroundColor: roleTarget?.action === 'promote' ? '#2E5C41' : '#8A2E17',
                  fontFamily: 'Manrope',
                  fontWeight: 600
                }}
                data-testid="confirm-role-change"
              >
                {roleTarget?.action === 'promote' ? 'Make Admin' : 'Revoke Access'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, color, bg }) => (
  <div className="p-4 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }}>
    <div className="flex items-center gap-2 mb-2" style={{ color }}>
      {icon}
      <span className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '11px', fontWeight: 600, letterSpacing: '0.15em' }}>
        {label}
      </span>
    </div>
    <div style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, fontSize: '32px', color: '#1A1A1A' }}>
      {value}
    </div>
  </div>
);

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    className="flex items-center px-4 py-3"
    style={{
      fontFamily: 'Manrope',
      fontWeight: 600,
      fontSize: '14px',
      color: active ? '#D95A41' : '#5C5C5C',
      borderBottom: active ? '2px solid #D95A41' : '2px solid transparent',
      background: 'none'
    }}
    data-testid={testId}
  >
    {children}
  </button>
);

const EmptyState = ({ message, testId }) => (
  <div className="text-center py-16 rounded-md border border-[#E5E0D8]" style={{ backgroundColor: '#FFFFFF' }} data-testid={testId}>
    <p style={{ fontFamily: 'Manrope', color: '#5C5C5C', fontSize: '16px' }}>{message}</p>
  </div>
);

const AdminListingRow = ({ listing, actions }) => (
  <div
    className="flex flex-col md:flex-row md:items-center gap-4 p-4 rounded-md border border-[#E5E0D8]"
    style={{ backgroundColor: '#FFFFFF' }}
    data-testid={`admin-row-${listing.id}`}
  >
    <div className="w-full md:w-24 h-24 rounded overflow-hidden flex-shrink-0">
      <img
        src={listing.images?.[0]
          ? `${process.env.REACT_APP_BACKEND_URL}${listing.images[0]}`
          : 'https://images.pexels.com/photos/1640775/pexels-photo-1640775.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200'}
        alt={listing.title}
        className="w-full h-full object-cover"
      />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <StatusPill status={listing.status || 'pending'} />
        <span style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#5C5C5C' }}>{listing.category}</span>
      </div>
      <h3 className="text-xl tracking-tight" style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}>
        {listing.title}
      </h3>
      <div className="flex items-center gap-2 mt-1">
        <Avatar name={listing.seller_name} imageUrl={listing.seller_profile_picture} size={20} />
        <span style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }}>
          {listing.seller_name} · ${listing.price} · Qty {listing.quantity} · {listing.location}
        </span>
      </div>
      {listing.rejection_reason && (
        <p className="mt-1" style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#991B1B' }}>
          Rejected: {listing.rejection_reason}
        </p>
      )}
    </div>
    <div className="flex flex-wrap gap-2 md:justify-end">{actions}</div>
  </div>
);


const ROLE_STYLES = {
  admin: { bg: '#F2E4C9', color: '#7A5A0F' },
  seller: { bg: '#FCE7DE', color: '#8A2E17' },
  buyer: { bg: '#E5EFE4', color: '#2E5C41' }
};

const RolePill = ({ role, isAdmin }) => {
  const key = isAdmin ? 'admin' : (role || 'buyer').toLowerCase();
  const s = ROLE_STYLES[key] || ROLE_STYLES.buyer;
  return (
    <span
      className="uppercase px-2 py-1 rounded"
      style={{ backgroundColor: s.bg, color: s.color, fontFamily: 'Manrope', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em' }}
    >
      {isAdmin ? 'Admin' : (role || 'buyer')}
    </span>
  );
};

const UsersTable = ({ users, currentUserId, onRemove, onRoleChange, processingId }) => (
  <div className="rounded-md border border-[#E5E0D8] overflow-hidden" style={{ backgroundColor: '#FFFFFF' }} data-testid="users-list">
    {/* Desktop header */}
    <div
      className="hidden md:grid px-4 py-3 border-b border-[#E5E0D8]"
      style={{ gridTemplateColumns: '2fr 2fr 1.3fr 1.3fr 0.9fr 220px', backgroundColor: '#F9F6F0' }}
    >
      {['User', 'Email', 'Phone', 'Location', 'Role', 'Actions'].map((h, i) => (
        <div key={i} className="uppercase" style={{ fontFamily: 'Manrope', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', color: '#5C5C5C' }}>
          {h}
        </div>
      ))}
    </div>

    {users.map((u) => {
      const isAdmin = !!u.is_admin;
      const isRoot = !!u.is_root_admin;
      const isSelf = u.id === currentUserId;
      const isProcessing = processingId === u.id;

      // Remove: locked for root admin and for self
      const removeDisabled = isRoot || isSelf || isProcessing;
      const removeTitle = isRoot
        ? 'Root admin cannot be removed'
        : isSelf ? 'You cannot remove yourself' : 'Remove user';

      // Role toggle: promote when non-admin, demote when admin (unless root/self)
      const showDemote = isAdmin && !isRoot && !isSelf;
      const showPromote = !isAdmin;
      const roleAction = showPromote ? 'promote' : 'demote';
      const roleLabel = showPromote ? 'Make Admin' : 'Revoke Admin';
      const roleDisabled = isProcessing || (!showPromote && !showDemote);
      const roleTitle = isRoot
        ? 'Root admin is permanent'
        : isSelf ? 'You cannot change your own admin access' : roleLabel;

      const RoleButton = ({ testId }) => (
        <Button
          onClick={() => onRoleChange(u, roleAction)}
          disabled={roleDisabled}
          variant="outline" size="sm"
          className="rounded-md"
          style={{
            borderColor: roleDisabled ? '#E5E0D8' : (showPromote ? '#2E5C41' : '#8A2E17'),
            color: roleDisabled ? '#B8B0A4' : (showPromote ? '#2E5C41' : '#8A2E17'),
            fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px',
            cursor: roleDisabled ? 'not-allowed' : 'pointer'
          }}
          title={roleTitle}
          data-testid={testId}
        >
          {showPromote ? <Shield size={14} className="mr-1" /> : <ShieldOff size={14} className="mr-1" />}
          {roleLabel}
        </Button>
      );

      const RemoveButton = ({ testId }) => (
        <Button
          onClick={() => onRemove(u)}
          disabled={removeDisabled}
          variant="outline" size="sm"
          className="rounded-md"
          style={{
            borderColor: removeDisabled ? '#E5E0D8' : '#991B1B',
            color: removeDisabled ? '#B8B0A4' : '#991B1B',
            fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px',
            cursor: removeDisabled ? 'not-allowed' : 'pointer'
          }}
          title={removeTitle}
          data-testid={testId}
        >
          <UserMinus size={14} className="mr-1" />
          Remove
        </Button>
      );

      return (
        <div key={u.id} className="border-b border-[#E5E0D8] last:border-b-0" data-testid={`user-row-${u.id}`}>
          {/* Desktop row */}
          <div
            className="hidden md:grid items-center gap-3 px-4 py-4"
            style={{ gridTemplateColumns: '2fr 2fr 1.3fr 1.3fr 0.9fr 220px' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <Avatar name={u.name} imageUrl={u.profile_picture} size={36} />
              <div className="min-w-0">
                <div className="truncate flex items-center gap-1.5" style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }} data-testid={`user-name-${u.id}`}>
                  <span className="truncate">{u.name || '—'}</span>
                  {isSelf && (
                    <span style={{ fontFamily: 'Manrope', fontSize: '10px', color: '#5C5C5C', fontWeight: 500 }}>(you)</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 min-w-0" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }} data-testid={`user-email-${u.id}`}>
              <Mail size={13} strokeWidth={1.5} className="flex-shrink-0" />
              <span className="truncate">{u.email}</span>
            </div>
            <div className="flex items-center gap-1.5" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }} data-testid={`user-phone-${u.id}`}>
              {u.phone ? (<><Phone size={13} strokeWidth={1.5} /><span>{u.phone}</span></>) : <span style={{ color: '#B8B0A4' }}>—</span>}
            </div>
            <div className="flex items-center gap-1.5 min-w-0" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }}>
              {u.location ? (<><MapPin size={13} strokeWidth={1.5} className="flex-shrink-0" /><span className="truncate">{u.location}</span></>) : <span style={{ color: '#B8B0A4' }}>—</span>}
            </div>
            <div className="flex flex-col gap-1">
              <RolePill role={u.role} isAdmin={isAdmin} />
              {isRoot && (
                <span
                  style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 600, color: '#7A5A0F', letterSpacing: '0.08em' }}
                  data-testid={`root-admin-badge-${u.id}`}
                >
                  ROOT
                </span>
              )}
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <RoleButton testId={`role-toggle-${u.id}`} />
              <RemoveButton testId={`remove-user-${u.id}`} />
            </div>
          </div>

          {/* Mobile card */}
          <div className="md:hidden p-4">
            <div className="flex items-start gap-3">
              <Avatar name={u.name} imageUrl={u.profile_picture} size={40} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
                  <div className="truncate flex items-center gap-1.5" style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }} data-testid={`user-name-mobile-${u.id}`}>
                    <span className="truncate">{u.name || '—'}</span>
                    {isSelf && (<span style={{ fontFamily: 'Manrope', fontSize: '10px', color: '#5C5C5C', fontWeight: 500 }}>(you)</span>)}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RolePill role={u.role} isAdmin={isAdmin} />
                    {isRoot && <span style={{ fontFamily: 'Manrope', fontSize: '10px', fontWeight: 600, color: '#7A5A0F' }}>ROOT</span>}
                  </div>
                </div>
                <div className="space-y-1" style={{ fontFamily: 'Manrope', fontSize: '13px', color: '#5C5C5C' }}>
                  <div className="flex items-center gap-2 min-w-0"><Mail size={13} strokeWidth={1.5} /><span className="truncate">{u.email}</span></div>
                  {u.phone && <div className="flex items-center gap-2"><Phone size={13} strokeWidth={1.5} /><span>{u.phone}</span></div>}
                  {u.location && <div className="flex items-center gap-2 min-w-0"><MapPin size={13} strokeWidth={1.5} /><span className="truncate">{u.location}</span></div>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <RoleButton testId={`role-toggle-mobile-${u.id}`} />
                  <RemoveButton testId={`remove-user-mobile-${u.id}`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);
